import clsx from 'clsx';
import * as React from 'react';

import { BLOCK_COLUMN, BLOCK_PARAGRAPH, BLOCK_TABS_ITEM } from '@gitbook/doc-core';
import { hideOnDrag, type PaletteDef, usePaletteHandle } from '@gitbook/react-uikit';
import { BlockRange, isBlockRangeSelection } from '@gitbook/slate';
import type { BlockProps } from '@gitbook/slate-react';
import { Icon } from '@gitbook/spine-icons';
import { Button, prettifyShortcut } from '@gitbook/spine-react';
import { useEventCallback, useMergedRefs } from '@gitbook/util-react-hooks';

import { shortcuts, useKeyboardShortcuts } from '../../../../Keyboard';
import type { DraggableState } from '../../../DragAndDrop';
import { useBlockPaletteFactory } from '../blockPalette';
import type { BlockLayout } from '../layout';
import { type PositionType, useInsertPaletteFactory } from '../useInsertPalette';

export type BlockControlsProps = {
    isHovered: boolean;
    layout: NonNullable<BlockLayout['controlButtons']>;
    children?: React.ReactNode;
};

export type BlockControlsRef = {
    openCommandPalette: (palette?: PaletteDef) => void;
};

interface BlockControlsContextProps {
    layout: NonNullable<BlockLayout['controlButtons']>;
    onPaletteStateChanged: (key: 'insert' | 'options', opened: boolean) => void;
}

const ButtonControlsContext = React.createContext<BlockControlsContextProps | null>(null);

function ButtonControlsProvider({
    children,
    layout,
    onPaletteStateChanged,
}: BlockControlsContextProps & { children: React.ReactNode }) {
    const value = React.useMemo(
        () => ({
            layout,
            onPaletteStateChanged,
        }),
        [layout, onPaletteStateChanged]
    );
    return (
        <ButtonControlsContext.Provider value={value}>{children}</ButtonControlsContext.Provider>
    );
}

export const useButtonControlsContext = () => {
    const context = React.useContext(ButtonControlsContext);
    if (!context) {
        throw new Error('useButtonControlsContext must be used within a ButtonControlsProvider');
    }
    return context;
};

/*
 * BlockControls is a wrapper component that handles the hover state of the
 * controls available for manipulating a block.
 *
 * A block can have an insert button and an options button
 * A block can have an insert button but no options button
 * A block can have an options button but no insert button
 *
 * Each triggers a different palette but the state (visibility)
 * of boths buttons depends on whether either palette is open.
 */
export const BlockControls = React.memo(
    function BlockControlsImpl(props: BlockControlsProps) {
        const { layout, isHovered, children } = props;

        const [paletteState, setPaletteState] = React.useState<'insert' | 'options' | null>(null);

        const onPaletteStateChanged = React.useCallback(
            (key: 'insert' | 'options', opened: boolean) => {
                setPaletteState((state) => {
                    if (!opened) {
                        return state === key ? null : state;
                    }
                    return key;
                });
            },
            []
        );

        const isVisible = isHovered || paletteState !== null;
        const withSlide = layout.withInsertButton && layout.withOptionsButton;

        return (
            <div
                contentEditable={false}
                className="absolute z-[5] bg-transparent"
                style={layout.hitArea.style}
            >
                <div
                    className={clsx(
                        'relative flex w-full select-none transition',
                        layout.withInsertButton && layout.withOptionsButton ? 'gap-1' : null,
                        isVisible ? 'opacity-100' : 'opacity-0',
                        withSlide && !isVisible && 'translate-x-1.5',
                        withSlide ? 'justify-end' : 'justify-start'
                    )}
                >
                    <ButtonControlsProvider
                        layout={layout}
                        onPaletteStateChanged={onPaletteStateChanged}
                    >
                        {children}
                    </ButtonControlsProvider>
                </div>
            </div>
        );
    },
    (prevProps, nextProps) =>
        prevProps.isHovered === nextProps.isHovered && prevProps.layout === nextProps.layout
);

export const InsertButton = React.memo(
    React.forwardRef<BlockControlsRef, Pick<BlockProps, 'editor' | 'node' | 'parent'>>(
        function InsertButton(props, ref) {
            const { editor, parent, node } = props;
            const { layout, onPaletteStateChanged } = useButtonControlsContext();

            const insertButtonRef = React.useRef<HTMLButtonElement>(null);
            const insertPaletteHandle = usePaletteHandle(insertButtonRef);
            const insertPositionRef = React.useRef<PositionType>('before');
            const isEmptyParagraph = node.type === BLOCK_PARAGRAPH && node.isEmpty;
            const mode = isEmptyParagraph ? 'replace' : 'insert';
            const getPosition = React.useCallback(
                () => (isEmptyParagraph ? 'replace' : insertPositionRef.current),
                [isEmptyParagraph]
            );
            const [modals, createInsertPalette] = useInsertPaletteFactory({
                editor,
                parent,
                node,
                getPosition,
                anchorRef: insertButtonRef,
            });

            const openCommandPalette = useEventCallback(() => {
                insertPaletteHandle.open(createInsertPalette());
            });

            React.useImperativeHandle(ref, () => ({ openCommandPalette }), [openCommandPalette]);

            React.useEffect(() => {
                onPaletteStateChanged('insert', insertPaletteHandle.opened);
            }, [onPaletteStateChanged, insertPaletteHandle.opened]);

            return (
                <>
                    {layout.withInsertButton ? (
                        <Button
                            ref={insertButtonRef}
                            data-exclude-from-copy
                            contentEditable={false}
                            kind="blank"
                            iconOnly
                            leadingIcon={Icon.Plus}
                            label="Insert block"
                            tooltip={
                                mode === 'replace'
                                    ? { label: 'Click to add' }
                                    : {
                                          label: 'Click to add below',
                                          description: `${prettifyShortcut('alt')} + click to add above`,
                                      }
                            }
                            onPressStart={(event) => {
                                insertPositionRef.current = event.altKey ? 'before' : 'after';
                            }}
                            onPress={() => openCommandPalette()}
                            size={layout.size}
                            className={hideOnDrag}
                        />
                    ) : (
                        /*
                            Hidden button used as palette anchor, e.g. 
                            an empty block inside an expandable doesn't show an insert button
                            but a user can trigger the insert palette with `/`. 
                        */
                        <button ref={insertButtonRef} type="button" className="size-0" />
                    )}
                    {modals}
                </>
            );
        }
    )
);

export const OptionsButton = React.memo(
    React.forwardRef<
        HTMLButtonElement,
        {
            isDragging: boolean;
            dragListeners: DraggableState['listeners'];
            isFocused: boolean;
            palette: (() => PaletteDef) | undefined;
        } & Pick<BlockProps, 'editor' | 'node' | 'parent'>
    >(function OptionsButton(props, ref) {
        const { dragListeners, editor, isDragging, isFocused, node, palette, parent } = props;
        const { layout, onPaletteStateChanged } = useButtonControlsContext();

        const optionsButtonRef = React.useRef<HTMLButtonElement>(null);
        const optionsPaletteHandle = usePaletteHandle(optionsButtonRef);
        const createBlockPalette = useBlockPaletteFactory({ nodeKey: node.key });
        const mergedRefs = useMergedRefs(ref, optionsButtonRef);

        const openPalette = useEventCallback(() => {
            const paletteDef = palette?.() ?? createBlockPalette();
            if (!paletteDef) {
                return;
            }
            optionsPaletteHandle.open({
                ...paletteDef,
                position: {
                    ...paletteDef.position,
                    preferredPlacement: paletteDef.position?.preferredPlacement ?? 'left',
                },
            });
            // Select the block when options palette is opened
            // expect when multiple blocks are selected.
            if (!isBlockRangeSelection(editor.value.selection)) {
                // TEMP: selection in tabs items with a single node requires reworking logic in `BlockRange.getBlocks`.
                // We block selection in this edge case in the meantime.
                if (
                    parent.nodes.size === 1 &&
                    parent.type &&
                    [BLOCK_TABS_ITEM, BLOCK_COLUMN].includes(parent.type)
                ) {
                    return;
                }

                editor.change((change) => {
                    change.selectBlock(
                        BlockRange.create({
                            anchorBlock: node.key,
                            focusBlock: node.key,
                            isFocused: true,
                        })
                    );
                });
            }
        });

        useKeyboardShortcuts(
            isFocused ? { [shortcuts.EDITOR_MODIFIER_PALETTE]: openPalette } : {},
            'editor-selection-collapsed'
        );

        React.useEffect(() => {
            onPaletteStateChanged('options', optionsPaletteHandle.opened);
        }, [onPaletteStateChanged, optionsPaletteHandle.opened]);

        return layout.withOptionsButton ? (
            <Button
                ref={mergedRefs}
                data-exclude-from-copy
                contentEditable={false}
                kind="blank"
                size={layout.size}
                iconOnly
                label="Block controls"
                leadingIcon={Icon.GrabberVertical}
                tooltip={{
                    label: 'Click for block options',
                    description: 'Drag to move',
                    shortcut: shortcuts.EDITOR_MODIFIER_PALETTE,
                    disabled: isDragging,
                }}
                onPress={openPalette}
                className={clsx(hideOnDrag, isDragging ? 'cursor-grabbing' : 'cursor-grab')}
                {...dragListeners}
            />
        ) : null;
    })
);
